import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { Ng2StateDeclaration, UIRouterModule } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { SpacesService } from '../../common/components/spaces/spaces.service';
import { Permissions } from 'account-hybrid/core/authentication';
import { SpaceType } from 'account-hybrid/common/components/spaces/models/space-type';
import { AccountSharedModule } from 'apps/account/src/account-shared';
import { AccountCommonModule } from '../../common/common.module';
import { AdvocacyGroupComponent } from './advocacy-group.component';
import { AudiencesComponent } from './audiences/audiences.component';
import { BigPictureComponent } from './big-picture/big-picture.component';
import { ContestsComponent } from './contests/contests.component';
import { ContestsListComponent } from './contests-list/contests-list.component';
import { JourneysComponent } from './journeys/journeys.component';
// Deep import: FtCampaignListComponent lives in legacy-shims. It is now STANDALONE
// (imports CommonModule + LegacyShimsModule itself) so it can be reused by the
// platform layer campaigns sections too — imported below rather than declared.
// Kept out of LegacyShimsModule's own declarations because a pipe-referencing
// component in that early-evaluated barrel module crashes the SPA via eval-order
// perturbation. See [[project_account_barrel_eval_order_fragility]].
import { FtCampaignListComponent } from 'apps/account/src/account-shared/legacy-shims/campaign-list.component';
// Shared native guest-detail modal — the advocate-detail states below open the
// SAME modal as People/Stories (M5b). Importing PeopleDetailsModule puts the
// native DetailsComponent in scope so `advocacy.*.advocate` no longer needs the
// AngularJS `ftPeopleDetails`.
import { PeopleDetailsModule } from '../people/details/people-details.module';
import { DetailsComponent } from '../people/details/details.component';
import { FtContestContainerComponent } from './contest-sheets/contest-container.component';
// WS2 strangle of AngularJS GroupAdvocacy: native NgRx feature owning the
// advocacy view + apiData slices, exposed to the components via a $ngRedux-shaped
// facade (advocacy-ngredux.facade.ts).
import { ADVOCACY_GROUP_FEATURE, advocacyGroupReducers } from './store/advocacy-group.reducers';
import { AdvocacyNgReduxFacade } from './store/advocacy-ngredux.facade';
// WS5 Phase 3 #3: the touchpoint trigger (legacy-shims FtTouchpointTriggerDirective,
// used ×32 in journeys.component.html — this module's only consumer) injects the
// NATIVE_TOUCHPOINT_CONTAINER leaf token; bind it here to the providedIn:'root'
// NativeTouchpointContainerService (createComponent mount). The directive cannot
// import the service directly without re-entering the account-shared barrel; this
// module evaluates after the barrel, so the binding is safe here.
import { NATIVE_TOUCHPOINT_CONTAINER } from './touchpoint-sheets/native-touchpoint-container.token';
import { NativeTouchpointContainerService } from './touchpoint-sheets/native-touchpoint-container.service';

/**
 * Native-Angular Advocacy (group) feature module — shell-first port of the
 * AngularJS `AccountApp.v2.GroupAdvocacy` module.
 *
 * Top-level Angular state `advocacy` is the new `AdvocacyGroupComponent`
 * shell (replacing the AngularJS `ftAdvocacy`). Every child state is now a
 * native `component:` (big-picture/contests/audiences/journeys + the
 * contest-container sub-sheets + the shared `DetailsComponent` advocate states,
 * re-pointed in M5b) — there are NO AngularJS `template:`/`controller:` leaves
 * left in this tree.
 *
 * WS5 router-severance (#87): both `<ft-ui-view-wrapper>` child outlets were
 * converted to native `@uirouter/angular` `<ui-view>` (bottom-up: contests'
 * nested outlet first, then AdvocacyGroupComponent's outer outlet) now that all
 * children are native. audiences' child outlet was already a bare `<ui-view>`.
 *
 * The AngularJS `ftAdvocacy` component registration + the 14
 * `$stateRegistryProvider.register(...)` calls have been removed from
 * account-v2/.../advocacy/group/{advocacy.module,state.config}.ts.
 *
 * Per-route parity gates flip to `['pixel','css']` because the shell's DOM
 * framework attrs differ across the rewrite (M3 People proved this).
 */

// WS5 #10 Stage B B5c: ng1 string-token resolves → native Ng2 form (Transition +
// ParseService + SpacesService class tokens). The `close` resolve's $state.go('^') →
// trans.router.stateService.go('^') (the same singleton StateService, same parent-relative
// '^'). Behaviour-identical in hybrid AND post-cut; feeds 8 contest/audience states.
function contestResolves(activeSection: string) {
  return [
    { token: 'propertyUuid', deps: [SpacesService], resolveFn: (spacesService: SpacesService) => spacesService.getPropertyUuid() },
    { token: 'contestUuid', deps: [Transition], resolveFn: (trans: Transition) => trans.params().contestUuid },
    { token: 'activeSection', deps: [], resolveFn: () => activeSection },
    { token: 'close', deps: [Transition], resolveFn: (trans: Transition) => () => trans.router.stateService.go('^') },
  ];
}

const ADVOCATE_UUID_RESOLVE = [
  { token: 'advocateUuid', deps: [Transition], resolveFn: (trans: Transition) => trans.params().advocateUUID },
  { token: 'curate', deps: [Transition, ParseService], resolveFn: (trans: Transition, parse: ParseService) => parse.bool(trans.params().curate) },
];

const UUID_RX = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

@NgModule({
  declarations: [
    AdvocacyGroupComponent,
    AudiencesComponent,
    BigPictureComponent,
    ContestsComponent,
    ContestsListComponent,
    JourneysComponent,
  ],
  // CUSTOM_ELEMENTS_SCHEMA (matching AccountSharedModule) tolerates only the
  // remaining HYPHENATED AngularJS-only tags that don't render at QA-login and
  // are deferred to later milestones: the <ft-modal> shells (People M5b — see
  // audiences.component.html:1607+). (The <ui-view> child outlets are NOT
  // schema-tolerated — they resolve as the @uirouter/angular UIView directive
  // via UIRouterModule.forChild below.) The four bare projection-slot tags (<collapsed>/<primary>/
  // <secondary>/<panel>) became known elements via LegacyShimsModule's
  // expandable-slots directives + PanelComponent — that's what let this drop
  // from NO_ERRORS_SCHEMA. The extends-via-barrel TDZ that previously blocked
  // adding those shims was fixed at the root (deep-path UnsubscribeComponent
  // imports in context-selector / mission-control). See HANDOFF §6.1.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    StoreModule.forFeature(ADVOCACY_GROUP_FEATURE, advocacyGroupReducers),
    AccountSharedModule,
    AccountCommonModule,
    PeopleDetailsModule,
    // FtCampaignListComponent is now STANDALONE (was declared here) so the platform
    // layer leaves can reuse it; imported here instead of declared.
    FtCampaignListComponent,
    FtContestContainerComponent,
    UIRouterModule.forChild({
      states: [
        {
          name: 'advocacy',
          url: '/{type:\\w}/{slug}/advocacy',
          params: { type: { dynamic: true }, slug: { dynamic: true } },
          component: AdvocacyGroupComponent,
          data: {
            requiresAuth: true,
            requiredSpaceType: [SpaceType.Property, SpaceType.Mixed, SpaceType.Company],
            requiredPermissions: Permissions.AdvocacyRead,
            pageClass: 'ft-advocacy-body',
          },
        },
        // advocacy.big-picture — first leaf ported to native Angular.
        { name: 'advocacy.big-picture', url: '/big-picture', component: BigPictureComponent },
        // advocacy.contests — second leaf ported to native Angular.
        { name: 'advocacy.contests', url: '/contests', component: ContestsComponent },
        {
          name: 'advocacy.contests.submissions',
          url: `/{contestUuid:${UUID_RX}}/submissions`,
          component: FtContestContainerComponent,
          resolve: contestResolves('submissions'),
        },
        {
          name: 'advocacy.contests.winners',
          url: `/{contestUuid:${UUID_RX}}/winners`,
          component: FtContestContainerComponent,
          resolve: contestResolves('winners'),
        },
        {
          name: 'advocacy.contests.details',
          url: `/{contestUuid:${UUID_RX}}/details`,
          component: FtContestContainerComponent,
          resolve: contestResolves('details'),
        },
        {
          name: 'advocacy.contests.invitations',
          url: `/{contestUuid:${UUID_RX}}/invitations`,
          component: FtContestContainerComponent,
          resolve: contestResolves('invitations'),
        },
        {
          name: 'advocacy.contests.advocate',
          url: `/{advocateUUID:${UUID_RX}}?curate`,
          component: DetailsComponent,
          resolve: ADVOCATE_UUID_RESOLVE,
        },
        // advocacy.audiences — third leaf ported to native Angular. Most
        // template content is gated on *Advocacy.isActive flags false at QA;
        // see schemas note above + AudiencesComponent jsdoc for what remains
        // un-native (deferred until a property with active advocacy is
        // recorded into the harness).
        { name: 'advocacy.audiences', url: '/audiences', component: AudiencesComponent },
        {
          name: 'advocacy.audiences.submissions',
          url: `/{contestUuid:${UUID_RX}}/submissions`,
          component: FtContestContainerComponent,
          resolve: contestResolves('submissions'),
        },
        {
          name: 'advocacy.audiences.winners',
          url: `/{contestUuid:${UUID_RX}}/winners`,
          component: FtContestContainerComponent,
          resolve: contestResolves('winners'),
        },
        {
          name: 'advocacy.audiences.details',
          url: `/{contestUuid:${UUID_RX}}/details`,
          component: FtContestContainerComponent,
          resolve: contestResolves('details'),
        },
        {
          name: 'advocacy.audiences.invitations',
          url: `/{contestUuid:${UUID_RX}}/invitations`,
          component: FtContestContainerComponent,
          resolve: contestResolves('invitations'),
        },
        {
          name: 'advocacy.audiences.advocate',
          url: `/{advocateUUID:${UUID_RX}}?curate`,
          component: DetailsComponent,
          resolve: ADVOCATE_UUID_RESOLVE,
        },
        // advocacy.journeys — fourth leaf ported to native Angular. Both
        // blockers landed in legacy-shims: FtCarouselCards family (carousel
        // positioning + active-card class management) AND FtStates family
        // (FtStatesService + ft-state / ft-set-state / ft-state-class
        // directives — the journeys body's conditional panels react to
        // these classes for v1-parity rendering).
        { name: 'advocacy.journeys', url: '/journeys', component: JourneysComponent },
      ] as Ng2StateDeclaration[],
    }),
  ],
  providers: [
    AdvocacyNgReduxFacade,
    { provide: NATIVE_TOUCHPOINT_CONTAINER, useExisting: NativeTouchpointContainerService },
  ],
})
export class AdvocacyGroupModule {}
